import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import { FaEye, FaTrash, FaSearch, FaDownload } from "react-icons/fa";
import { MdCheckCircle, MdPending, MdCancel, MdEdit } from "react-icons/md";

// ✅ Base URL dynamique (dev/prod)
const API_BASE_URL =
  (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "") ||
  "https://nouzhaback-theta.vercel.app";

// ✅ helper fetch centralisé
async function apiFetch(path, { token, method = "GET", body } = {}) {
  const url = `${API_BASE_URL}${path.startsWith("/") ? "" : "/"}${path}`;

  const res = await fetch(url, {
    method,
    headers: {
      Authorization: token ? `Bearer ${token}` : "",
      "Content-Type": "application/json",
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  // Gestion 401
  if (res.status === 401) {
    throw new Error("UNAUTHORIZED");
  }

  const contentType = res.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    throw new Error("NOT_JSON");
  }

  const data = await res.json();

  if (!res.ok) {
    const msg = data?.message || `HTTP error ${res.status}`;
    throw new Error(msg);
  }

  return data;
}

const AgencyPartnershipAdmin = () => {
  const [partnerships, setPartnerships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPartnership, setSelectedPartnership] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [viewMode, setViewModeState] = useState(() => {
    try { return localStorage.getItem("adminViewMode") || "cards"; } catch { return "cards"; }
  });
  const setViewMode = (v) => { setViewModeState(v); try { localStorage.setItem("adminViewMode", v); } catch (_) {} };
  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    statut: "tous",
    pays: "tous",
    search: "",
  });
  const [stats, setStats] = useState({});
  const [pagination, setPagination] = useState({});

  // Récupérer les demandes de partenariat
  const fetchPartnerships = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      if (!token) {
        toast.error("Vous devez être connecté pour accéder à cette page");
        window.location.href = "/login";
        return;
      }

      const queryParams = new URLSearchParams({
        page: String(filters.page),
        limit: String(filters.limit),
        ...(filters.statut !== "tous" ? { statut: filters.statut } : {}),
        ...(filters.pays !== "tous" ? { pays: filters.pays } : {}),
        ...(filters.search ? { search: filters.search } : {}),
      });

      const data = await apiFetch(
        `/agency-partnership/admin/list?${queryParams.toString()}`,
        { token }
      );

      if (data.success) {
        setPartnerships(data.data.partnerships || []);
        setPagination(data.data.pagination || {});
      } else {
        toast.error("Erreur lors du chargement des demandes");
      }
    } catch (error) {
      console.error("Erreur:", error);

      if (String(error.message) === "UNAUTHORIZED") {
        toast.error("Session expirée, veuillez vous reconnecter");
        return;
      }
      if (String(error.message) === "NOT_JSON") {
        toast.error("Réponse serveur invalide (pas du JSON)");
        return;
      }
      toast.error("Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  // Récupérer les statistiques
  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const data = await apiFetch("/agency-partnership/admin/stats", { token });
      if (data.success) setStats(data.data || {});
    } catch (error) {
      console.error("Erreur stats:", error);
      if (String(error.message) === "UNAUTHORIZED") {
        toast.error("Session expirée, veuillez vous reconnecter");
      }
    }
  };

  // Mettre à jour le statut d'une demande
  const updateStatus = async (id, newStatus, notes = "") => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Vous devez être connecté pour effectuer cette action");
        return;
      }

      const data = await apiFetch(`/agency-partnership/admin/${id}/status`, {
        token,
        method: "PUT",
        body: { statut: newStatus, notes_admin: notes },
      });

      if (data.success) {
        toast.success("Statut mis à jour avec succès");
        fetchPartnerships();
        setShowModal(false);
      } else {
        toast.error("Erreur lors de la mise à jour");
      }
    } catch (error) {
      console.error("Erreur:", error);
      if (String(error.message) === "UNAUTHORIZED") {
        toast.error("Session expirée, veuillez vous reconnecter");
        return;
      }
      toast.error("Erreur de connexion");
    }
  };

  // Supprimer une demande
  const deletePartnership = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette demande ?")) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Vous devez être connecté pour effectuer cette action");
        return;
      }

      const data = await apiFetch(`/agency-partnership/admin/${id}`, {
        token,
        method: "DELETE",
      });

      if (data.success) {
        toast.success("Demande supprimée avec succès");
        fetchPartnerships();
      } else {
        toast.error("Erreur lors de la suppression");
      }
    } catch (error) {
      console.error("Erreur:", error);
      if (String(error.message) === "UNAUTHORIZED") {
        toast.error("Session expirée, veuillez vous reconnecter");
        return;
      }
      toast.error("Erreur de connexion");
    }
  };

  // Gérer les changements de filtres
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: 1,
    }));
  };

  // Gérer la pagination
  const handlePageChange = (newPage) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  // Obtenir l'icône du statut
  const getStatusIcon = (status) => {
    switch (status) {
      case "en_attente":
        return <MdPending className="text-yellow-500" />;
      case "en_cours":
        return <MdEdit className="text-blue-500" />;
      case "accepte":
        return <MdCheckCircle className="text-green-500" />;
      case "refuse":
        return <MdCancel className="text-red-500" />;
      default:
        return <MdPending className="text-slate-500" />;
    }
  };

  // Obtenir la couleur du statut
  const getStatusColor = (status) => {
    switch (status) {
      case "en_attente":
        return "bg-yellow-100 text-yellow-800";
      case "en_cours":
        return "bg-blue-100 text-blue-800";
      case "accepte":
        return "bg-green-100 text-green-800";
      case "refuse":
        return "bg-red-100 text-red-800";
      default:
        return "bg-slate-100 text-slate-800";
    }
  };

  // Formater la date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  useEffect(() => {
    fetchPartnerships();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const statusText = (s) => ({ en_attente: "En attente", en_cours: "En cours", accepte: "Accepté", refuse: "Refusé" }[s] || s || "—");
  const VIEW_OPTIONS = [{ id: "cards", label: "Cartes", icon: "▦" }, { id: "table", label: "Tableau", icon: "☰" }, { id: "compact", label: "Compact", icon: "≣" }];
  const ViewSwitcher = () => (
    <div className="inline-flex items-center bg-slate-100 rounded-xl p-1 gap-1">
      {VIEW_OPTIONS.map((v) => (
        <button key={v.id} onClick={() => setViewMode(v.id)} title={v.label} className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all flex items-center gap-1.5 ${viewMode === v.id ? "bg-white text-orange-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
          <span className="text-base leading-none">{v.icon}</span><span className="hidden xl:inline">{v.label}</span>
        </button>
      ))}
    </div>
  );
  const StatusBadge = ({ status }) => (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${getStatusColor(status)}`}>{getStatusIcon(status)}<span className="ml-1">{statusText(status)}</span></span>
  );
  const renderPActions = (p) => (
    <>
      <button onClick={() => { setSelectedPartnership(p); setShowModal(true); }} className="text-blue-700 px-3 py-1.5 rounded-lg border border-blue-200 bg-blue-50 hover:bg-blue-100 transition-all text-xs font-semibold inline-flex items-center gap-1"><FaEye /> Détails</button>
      <button onClick={() => deletePartnership(p._id)} className="text-red-700 px-3 py-1.5 rounded-lg border border-red-200 bg-red-50 hover:bg-red-100 transition-all text-xs font-semibold inline-flex items-center gap-1"><FaTrash /> Supprimer</button>
    </>
  );

  return (
    <div className="p-6">
      {/* En-tête avec statistiques */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-12 h-12 flex-none rounded-2xl bg-gradient-to-br from-orange-500 to-red-600 text-white flex items-center justify-center text-2xl shadow-md">🤝</span>
          <div>
            <h1 className="text-2xl lg:text-3xl font-black text-slate-800 leading-tight">Partenariats d&apos;Agences</h1>
            <p className="text-sm text-slate-500">Demandes de partenariat &amp; suivi</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all">
            <div className="flex items-center">
              <div className="w-11 h-11 flex items-center justify-center bg-gradient-to-br from-sky-500 to-blue-600 rounded-xl shadow-sm">
                <MdPending className="text-white text-xl" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-slate-500">
                  Total Demandes
                </p>
                <p className="text-2xl font-bold text-slate-900">
                  {stats.total_demandes || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all">
            <div className="flex items-center">
              <div className="w-11 h-11 flex items-center justify-center bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl shadow-sm">
                <MdPending className="text-white text-xl" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-slate-500">En Attente</p>
                <p className="text-2xl font-bold text-slate-900">
                  {stats.demandes_en_attente || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all">
            <div className="flex items-center">
              <div className="w-11 h-11 flex items-center justify-center bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl shadow-sm">
                <MdCheckCircle className="text-white text-xl" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-slate-500">Ce Mois</p>
                <p className="text-2xl font-bold text-slate-900">
                  {stats.demandes_ce_mois || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all">
            <div className="flex items-center">
              <div className="w-11 h-11 flex items-center justify-center bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl shadow-sm">
                <FaDownload className="text-white text-xl" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-slate-500">
                  7 Derniers Jours
                </p>
                <p className="text-2xl font-bold text-slate-900">
                  {stats.demandes_7_derniers_jours || 0}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Recherche
            </label>
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Nom d'agence, email..."
                className="pl-10 w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Statut
            </label>
            <select
              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={filters.statut}
              onChange={(e) => handleFilterChange("statut", e.target.value)}
            >
              <option value="tous">Tous les statuts</option>
              <option value="en_attente">En attente</option>
              <option value="en_cours">En cours</option>
              <option value="accepte">Accepté</option>
              <option value="refuse">Refusé</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Pays
            </label>
            <input
              type="text"
              placeholder="Filtrer par pays"
              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={filters.pays === "tous" ? "" : filters.pays}
              onChange={(e) =>
                handleFilterChange("pays", e.target.value || "tous")
              }
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Par page
            </label>
            <select
              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={filters.limit}
              onChange={(e) =>
                handleFilterChange("limit", parseInt(e.target.value, 10))
              }
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tableau des demandes */}
      <div className="flex items-center justify-between mb-3 px-1">
        <p className="text-sm text-slate-500"><span className="font-bold text-slate-700">{partnerships.length}</span> demande{partnerships.length !== 1 ? "s" : ""}</p>
        <ViewSwitcher />
      </div>
      <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
        {loading ? (
          <div className="p-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            <style>{`.nz-skel{position:relative;overflow:hidden;background:#eef2f7}.nz-skel::after{content:"";position:absolute;inset:0;transform:translateX(-100%);background:linear-gradient(90deg,transparent,rgba(255,255,255,.7),transparent);animation:nz-shimmer 1.4s infinite}@keyframes nz-shimmer{100%{transform:translateX(100%)}}`}</style>
            {[...Array(6)].map((_, i) => (
              <div key={i} className="rounded-2xl border border-slate-100 p-5 space-y-3">
                <div className="h-11 w-11 rounded-2xl nz-skel" />
                <div className="h-4 w-2/3 rounded nz-skel" />
                <div className="h-3 w-full rounded nz-skel" />
                <div className="h-3 w-5/6 rounded nz-skel" />
              </div>
            ))}
          </div>
        ) : partnerships.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-slate-500">
              Aucune demande de partenariat trouvée
            </p>
          </div>
        ) : (
          <>
            {viewMode === "table" ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-100">
                <thead className="bg-slate-50/80">
                  <tr>
                    {["Agence", "Pays", "Contact", "Statut", "Date", "Actions"].map((h) => (
                      <th key={h} className="px-6 py-3.5 text-left text-[11px] font-bold text-slate-400 uppercase tracking-[0.08em]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-100">
                  {partnerships.map((partnership) => (
                    <tr key={partnership._id} className="hover:bg-orange-50/40 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-slate-800">{partnership.nom_agence}</div>
                        {partnership.site_web && (<a href={partnership.site_web} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">{partnership.site_web}</a>)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{partnership.pays_origine}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-slate-800">{partnership.nom_contact}</div>
                        <div className="text-xs text-slate-400">{partnership.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap"><StatusBadge status={partnership.statut} /></td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-400">{formatDate(partnership.date_demande)}</td>
                      <td className="px-6 py-4 whitespace-nowrap"><div className="flex gap-2">{renderPActions(partnership)}</div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            ) : viewMode === "compact" ? (
              <div className="divide-y divide-slate-100">
                {partnerships.map((p) => (
                  <div key={p._id} className="flex items-center gap-3 px-4 lg:px-6 py-3 hover:bg-orange-50/40 transition-colors">
                    <div className="w-10 h-10 flex-none rounded-xl bg-gradient-to-br from-slate-700 to-slate-900 text-white flex items-center justify-center font-bold text-sm">{(p.nom_agence || "?").charAt(0).toUpperCase()}</div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-slate-800 text-sm truncate">{p.nom_agence}</p>
                      <p className="text-xs text-slate-400 truncate">{p.nom_contact} · {p.email} · {p.pays_origine}</p>
                    </div>
                    <StatusBadge status={p.statut} />
                    <div className="flex gap-1.5 flex-none">{renderPActions(p)}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-4 lg:p-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-5">
                {partnerships.map((p) => (
                  <div key={p._id} className="group bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 p-5 flex flex-col">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-11 h-11 flex-none rounded-2xl bg-gradient-to-br from-slate-700 to-slate-900 text-white flex items-center justify-center font-black">{(p.nom_agence || "?").charAt(0).toUpperCase()}</div>
                        <div className="min-w-0">
                          <p className="font-bold text-slate-800 truncate">{p.nom_agence}</p>
                          <p className="text-xs text-slate-400 truncate">{p.pays_origine}</p>
                        </div>
                      </div>
                      <StatusBadge status={p.statut} />
                    </div>
                    <div className="mt-3 space-y-1.5 text-xs flex-1">
                      <div className="flex items-center gap-2 text-slate-600"><span>👤</span><span className="truncate">{p.nom_contact}</span></div>
                      <div className="flex items-center gap-2 text-slate-600"><span>✉️</span><span className="truncate">{p.email}</span></div>
                      {p.site_web && (<div className="flex items-center gap-2 text-slate-600"><span>🌐</span><a href={p.site_web} target="_blank" rel="noopener noreferrer" className="truncate text-blue-600 hover:underline">{p.site_web}</a></div>)}
                      <div className="flex items-center gap-2 text-slate-400"><span>📅</span><span>{formatDate(p.date_demande)}</span></div>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-slate-100">{renderPActions(p)}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-slate-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => handlePageChange(pagination.current_page - 1)}
                    disabled={pagination.current_page === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-slate-300 text-sm font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-50"
                  >
                    Précédent
                  </button>
                  <button
                    onClick={() => handlePageChange(pagination.current_page + 1)}
                    disabled={
                      pagination.current_page === pagination.total_pages
                    }
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-slate-300 text-sm font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-50"
                  >
                    Suivant
                  </button>
                </div>

                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-slate-700">
                      Affichage de{" "}
                      <span className="font-medium">
                        {(pagination.current_page - 1) *
                          pagination.items_per_page +
                          1}
                      </span>{" "}
                      à{" "}
                      <span className="font-medium">
                        {Math.min(
                          pagination.current_page * pagination.items_per_page,
                          pagination.total_items
                        )}
                      </span>{" "}
                      sur{" "}
                      <span className="font-medium">
                        {pagination.total_items}
                      </span>{" "}
                      résultats
                    </p>
                  </div>

                  <div>
                    <nav
                      className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                      aria-label="Pagination"
                    >
                      <button
                        onClick={() =>
                          handlePageChange(pagination.current_page - 1)
                        }
                        disabled={pagination.current_page === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-slate-300 bg-white text-sm font-medium text-slate-500 hover:bg-slate-50 disabled:opacity-50"
                      >
                        Précédent
                      </button>

                      {[...Array(pagination.total_pages)].map((_, index) => {
                        const page = index + 1;
                        return (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              page === pagination.current_page
                                ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                                : "bg-white border-slate-300 text-slate-500 hover:bg-slate-50"
                            }`}
                          >
                            {page}
                          </button>
                        );
                      })}

                      <button
                        onClick={() =>
                          handlePageChange(pagination.current_page + 1)
                        }
                        disabled={
                          pagination.current_page === pagination.total_pages
                        }
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-slate-300 bg-white text-sm font-medium text-slate-500 hover:bg-slate-50 disabled:opacity-50"
                      >
                        Suivant
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal de détails */}
      {showModal && selectedPartnership && (
        <div className="fixed inset-0 bg-slate-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-slate-900">
                  Détails de la demande - {selectedPartnership.nom_agence}
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  ✕
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Informations de l'agence */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-slate-900">
                    Informations de l&apos;agence
                  </h4>
                  <div className="space-y-2">
                    <p>
                      <span className="font-medium">Nom:</span>{" "}
                      {selectedPartnership.nom_agence}
                    </p>
                    <p>
                      <span className="font-medium">Pays:</span>{" "}
                      {selectedPartnership.pays_origine}
                    </p>
                    <p>
                      <span className="font-medium">Site web:</span>
                      {selectedPartnership.site_web ? (
                        <a
                          href={selectedPartnership.site_web}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline ml-1"
                        >
                          {selectedPartnership.site_web}
                        </a>
                      ) : (
                        " Non renseigné"
                      )}
                    </p>
                    <p>
                      <span className="font-medium">Licence:</span>{" "}
                      {selectedPartnership.licence_numero || "Non renseigné"}
                    </p>
                    <p>
                      <span className="font-medium">Clients/an:</span>{" "}
                      {selectedPartnership.nb_clients_annuel || "Non renseigné"}
                    </p>
                    <p>
                      <span className="font-medium">
                        Commission souhaitée:
                      </span>{" "}
                      {selectedPartnership.commission_souhaitee}%
                    </p>
                    <p>
                      <span className="font-medium">
                        Volume d&apos;affaires estimé:
                      </span>{" "}
                      {selectedPartnership.volume_affaires_estime ||
                        "Non renseigné"}
                    </p>
                    <p>
                      <span className="font-medium">Expérience Maroc:</span>{" "}
                      {selectedPartnership.experience_maroc || "Non renseigné"}
                    </p>
                  </div>
                </div>

                {/* Contact */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-slate-900">Contact</h4>
                  <div className="space-y-2">
                    <p>
                      <span className="font-medium">Nom:</span>{" "}
                      {selectedPartnership.nom_contact}
                    </p>
                    <p>
                      <span className="font-medium">Email:</span>{" "}
                      {selectedPartnership.email}
                    </p>
                    <p>
                      <span className="font-medium">Téléphone:</span>{" "}
                      {selectedPartnership.telephone || "Non renseigné"}
                    </p>
                  </div>

                  <h4 className="font-semibold text-slate-900 mt-6">
                    Services
                  </h4>
                  <div className="space-y-2">
                    <p>
                      <span className="font-medium">Services proposés:</span>
                    </p>
                    <ul className="list-disc list-inside ml-4">
                      {selectedPartnership.type_services?.length ? (
                        selectedPartnership.type_services.map((service, idx) => (
                          <li key={idx}>{service}</li>
                        ))
                      ) : (
                        <li>Aucun service spécifié</li>
                      )}
                    </ul>

                    <p>
                      <span className="font-medium">Services requis:</span>
                    </p>
                    <ul className="list-disc list-inside ml-4">
                      {selectedPartnership.services_requis?.length ? (
                        selectedPartnership.services_requis.map((service, idx) => (
                          <li key={service._id || idx}>
                            <span className="font-medium">
                              {service.type_service}
                            </span>
                            {service.description && <> – {service.description}</>}
                            {service.volume_estime && (
                              <> – Volume estimé : {service.volume_estime}</>
                            )}
                            {service.periode && <> – Période : {service.periode}</>}
                          </li>
                        ))
                      ) : (
                        <li>Aucun service spécifié</li>
                      )}
                    </ul>

                    <p>
                      <span className="font-medium">Langues parlées:</span>
                    </p>
                    <ul className="list-disc list-inside ml-4">
                      {selectedPartnership.langues_parlees?.length ? (
                        selectedPartnership.langues_parlees.map((l, idx) => (
                          <li key={idx}>{l}</li>
                        ))
                      ) : (
                        <li>Aucune langue spécifiée</li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Présentation */}
              {selectedPartnership.presentation_agence && (
                <div className="mt-6">
                  <h4 className="font-semibold text-slate-900 mb-2">
                    Présentation de l&apos;agence
                  </h4>
                  <p className="text-slate-700 bg-slate-50 p-3 rounded">
                    {selectedPartnership.presentation_agence}
                  </p>
                </div>
              )}

              {/* Notes admin */}
              {selectedPartnership.notes_admin && (
                <div className="mt-6">
                  <h4 className="font-semibold text-slate-900 mb-2">
                    Notes administratives
                  </h4>
                  <p className="text-slate-700 bg-yellow-50 p-3 rounded border-l-4 border-yellow-400">
                    {selectedPartnership.notes_admin}
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  onClick={() =>
                    updateStatus(selectedPartnership._id, "en_cours")
                  }
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  disabled={selectedPartnership.statut === "en_cours"}
                >
                  Marquer en cours
                </button>
                <button
                  onClick={() =>
                    updateStatus(selectedPartnership._id, "accepte")
                  }
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  disabled={selectedPartnership.statut === "accepte"}
                >
                  Accepter
                </button>
                <button
                  onClick={() =>
                    updateStatus(selectedPartnership._id, "refuse")
                  }
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                  disabled={selectedPartnership.statut === "refuse"}
                >
                  Refuser
                </button>
              </div>

              {/* Informations système */}
              <div className="mt-6 pt-4 border-t border-slate-200 text-sm text-slate-500">
                <p>Demande créée le: {formatDate(selectedPartnership.date_demande)}</p>
                {selectedPartnership.date_traitement && (
                  <p>Dernière modification: {formatDate(selectedPartnership.date_traitement)}</p>
                )}
                {selectedPartnership.traite_par && (
                  <p>
                    Traité par: {selectedPartnership.traite_par.nom}{" "}
                    {selectedPartnership.traite_par.prenom}
                  </p>
                )}
                <p>IP: {selectedPartnership.ip_address}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgencyPartnershipAdmin;
